import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Headphones, X, Sparkles, Clock } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "her_streaming_banner_dismissed";

/**
 * Dual-chip pre-launch banner pinned above the site header.
 *
 *   LEFT chip  →  "Streaming · Season 1"       (plays latest released episode)
 *   RIGHT chip →  "Coming Soon · Season 2"    (scrolls to the Newsletter waitlist)
 *
 * When no Season 2 placeholder exists yet the banner falls back to the
 * original single "Now Streaming" state. Dismissal is persisted per-browser.
 */
export const CountdownBanner = () => {
    const [dismissed, setDismissed] = useState(
        () => typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1"
    );
    const [episodes, setEpisodes] = useState([]);
    const [foundingStatus, setFoundingStatus] = useState(null);
    const { playEpisode } = usePlayer();

    useEffect(() => {
        let mounted = true;
        axios
            .get(`${API}/episodes`)
            .then((r) => mounted && setEpisodes(r.data || []))
            .catch(() => {});
        axios
            .get(`${API}/newsletter/founding-status`)
            .then((r) => mounted && setFoundingStatus(r.data || null))
            .catch(() => {});
        return () => {
            mounted = false;
        };
    }, []);

    // Newest RELEASED episode with audio — powers the "Streaming" chip.
    const latestEpisode = useMemo(() => {
        const now = Date.now();
        const released = episodes.filter((ep) => {
            if (!ep.audio_url) return false;
            if (!ep.release_at) return true;
            const t = new Date(ep.release_at).getTime();
            return !Number.isNaN(t) && t <= now;
        });
        if (!released.length) return null;
        return released.reduce((best, ep) => {
            if (!best) return ep;
            if ((ep.number || 0) !== (best.number || 0))
                return (ep.number || 0) > (best.number || 0) ? ep : best;
            const a = ep.release_at ? new Date(ep.release_at).getTime() : 0;
            const b = best.release_at ? new Date(best.release_at).getTime() : 0;
            return a > b ? ep : best;
        }, null);
    }, [episodes]);

    // First unreleased Season 2 episode — powers the "Coming Soon" chip.
    const nextSeason2 = useMemo(() => {
        const now = Date.now();
        const s2 = episodes
            .filter((ep) => ep.season === 2 || (ep.number || 0) >= 7)
            .filter((ep) => {
                if (!ep.release_at) return false;
                const t = new Date(ep.release_at).getTime();
                return !Number.isNaN(t) && t > now;
            })
            .sort((a, b) => new Date(a.release_at) - new Date(b.release_at));
        return s2[0] || null;
    }, [episodes]);

    if (dismissed) return null;

    const dismiss = () => {
        localStorage.setItem(STORAGE_KEY, "1");
        setDismissed(true);
    };

    const playLatest = (e) => {
        e.preventDefault();
        if (latestEpisode) playEpisode(latestEpisode);
        const target = document.getElementById("episodes");
        if (target) target.scrollIntoView({ behavior: "smooth" });
    };

    const scrollToJoin = (e) => {
        e.preventDefault();
        const target = document.getElementById("join") || document.getElementById("newsletter");
        if (target) target.scrollIntoView({ behavior: "smooth" });
    };

    const formatDate = (iso) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                month: "short", day: "numeric",
            });
        } catch { return ""; }
    };

    return (
        <div
            data-testid="streaming-banner"
            className="relative z-50 w-full bg-gradient-to-r from-lilac-900 via-[#3F2655] to-lilac-700 text-white"
        >
            <div className="mx-auto max-w-7xl px-4 md:px-8 py-2.5 flex items-center gap-2 md:gap-3 flex-wrap">
                <div className="flex items-center gap-2 shrink-0">
                    <span className="grid place-items-center w-6 h-6 rounded-full bg-gold/25 border border-gold/40">
                        <Sparkles className="w-3 h-3 text-gold" />
                    </span>
                    <span className="eyebrow text-gold/90 hidden sm:inline text-[10px]"
                          data-testid="streaming-banner-eyebrow">
                        Becoming H.E.R.
                    </span>
                </div>

                {/* LEFT CHIP · Season 1 · Now Streaming */}
                <a
                    href="#episodes"
                    onClick={playLatest}
                    data-testid="streaming-banner-cta"
                    className="inline-flex items-center gap-1.5 rounded-full bg-gold/95 hover:bg-white hover:text-lilac-900 text-lilac-900 text-[11px] font-medium h-7 px-3 transition-colors whitespace-nowrap"
                >
                    <Headphones className="w-3 h-3" />
                    <span
                        className="uppercase tracking-wider text-[9px] font-semibold"
                        data-testid="streaming-banner-chip-s1-label"
                    >
                        S1 · Now Streaming
                    </span>
                    {latestEpisode && (
                        <span className="hidden md:inline text-lilac-900/85 font-normal ml-0.5">
                            · Play latest
                        </span>
                    )}
                </a>

                {/* RIGHT CHIP · Season 2 · Coming Soon */}
                {nextSeason2 ? (
                    <a
                        href="#join"
                        onClick={scrollToJoin}
                        data-testid="streaming-banner-chip-s2"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-gold/50 text-white text-[11px] font-medium h-7 px-3 transition-colors whitespace-nowrap"
                    >
                        <Clock className="w-3 h-3 text-gold" />
                        <span
                            className="uppercase tracking-wider text-[9px] font-semibold text-gold"
                            data-testid="streaming-banner-chip-s2-label"
                        >
                            S2 · Coming {formatDate(nextSeason2.release_at)}
                        </span>
                        {foundingStatus && (
                            <span
                                className="hidden md:inline uppercase tracking-wider text-[9px] font-semibold text-white/95 ml-0.5"
                                data-testid="streaming-banner-chip-s2-seats"
                            >
                                {foundingStatus.is_full
                                    ? "· Founding Full 💜"
                                    : `· ${foundingStatus.remaining} Seats Left`}
                            </span>
                        )}
                    </a>
                ) : null}

                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    data-testid="streaming-banner-dismiss"
                    className="ml-auto grid place-items-center w-7 h-7 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};
